import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { ScriptService } from 'app/shared/service/script.service';
import { getCompanyName, redirectToGlobalErrorPage, redirectToLogoutErrorPage } from 'app/shared/util/request-util';
import * as moment from 'moment'; // NOSONAR: This import is necessary for moment usage. Default import doesn't work.
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';

import { Account } from '../models/account.model';
import { AppConfiguration } from '../models/app-configuration.model';
import { AccountService } from './account.service';
import { AccountPanelEvent } from './auth.events';
import { StateStorageService } from './state-storage.service';
import { TranslateService } from '@ngx-translate/core';

const SESSION_EXPIRE_POPUP_KEY = 'session_expire-popup';
/**
 * Angular Service that implement oauth2 authentication methods
 */
@Injectable({ providedIn: 'root' })
export class AuthServerProvider {
  private static readonly EXPIRATION_TIMEOUT = 30000;
  private static readonly MILLISECOND_MULTIPLIER = 1000;

  private activeSessionTimeoutSubscription?: Subscription;

  public eventsSubject: Subject<AccountPanelEvent> = new Subject<AccountPanelEvent>();

  constructor(
    private readonly $localStorage: LocalStorageService,
    private readonly stateStorage: StateStorageService,
    private readonly oauthService: OAuthService,
    private readonly accountService: AccountService,
    public ngZone: NgZone,
    private readonly router: Router,
    private readonly appConfig: AppConfiguration,
    private readonly sessionStorage: SessionStorageService,
    private readonly scriptService: ScriptService,
    private readonly languageService: TranslateService
  ) { }

  /**
   * Method invoked on app initialization.
   * - Get app configuration with oauth2 server info
   * - Load discovery document and try to login user
   * - Fetch user if login successfully
   */
  async initAuth(): Promise<void> {
    return new Promise((resolveFn, rejectFn) => {
      const companyName = getCompanyName();

      if (!companyName) {
        this.redirectToErrorPage();
        rejectFn(new Error(''));
        return;
      }

      this.oauthService.configure(this.appConfig.authConfig);

      this.oauthService.loadDiscoveryDocument().then(() => {
        if (this.userJustLoggedIn()) {
          this.redirectToLoggedCompany(companyName);
          this.afterLoginFlowCompleted(false, resolveFn);
          return;
        }

        // prova a recuperare il token partendo dall'url, che dovrebbe contenere le informazioni
        this.oauthService
          .tryLoginCodeFlow()
          .then(() => {
            if (this.hasInvalidToken()) {
              this.handleLoginFailure();
              rejectFn(new Error(''));
            } else {
              this.stateStorage.storeLoggedCompany(companyName);
              this.afterLoginFlowCompleted(true, resolveFn);
              this.handleLoginSuccess();
            }
          })
          .catch(err => {
            // eslint-disable-next-line no-console
            console.log('Error on code flow: ' + err); // NOSONAR
            if (this.hasInvalidToken()) {
              this.handleLoginError(companyName);
              rejectFn(new Error(''));
            }
          });
      });
    });
  }

  /**
   * Load current authenticated user information, start session duration timer.
   * Update app state and send a login_completed events to all listener
   * @param firstLogin if is first login or user just loggedin
   */
  afterLoginFlowCompleted(firstLogin: boolean, resolveFn: (value: void | PromiseLike<void>) => void): void {
    this.accountService.identity(true).subscribe((account: Account | null) => {
      if (firstLogin) {
        const subscription = this.router.events.subscribe(event => {
          if (event instanceof NavigationEnd) {
            this.eventsSubject.next(new AccountPanelEvent('login_completed', { account }));
            subscription.unsubscribe();
          }
        });
        this.restoreExpirationSessionTime();
      }

      this.setupCloseSessionTimer();

      this.oauthService.setupAutomaticSilentRefresh();

      resolveFn();
    });
  }

  /**
   * Utility function for retrieving current token
   */
  getToken(): string {
    return this.oauthService.getAccessToken();
  }

  /**
   * Save date when session expiration must show
   */
  restoreExpirationSessionTime(): void {
    if (this.appConfig.sessionTimeout > 0) {
      this.$localStorage.store(
        SESSION_EXPIRE_POPUP_KEY,
        moment()
          .add(this.appConfig.sessionTimeout - AuthServerProvider.EXPIRATION_TIMEOUT, 'milliseconds')
          .toJSON()
      );
    }
  }

  /**
   * Start session expiration timer.
   * Thirty seconds before end session send a 'session_expires' event (@see EndSessionComponent)
   */
  setupCloseSessionTimer(): void {
    if (this.appConfig.sessionTimeout <= 0) {
      if (this.$localStorage.retrieve(SESSION_EXPIRE_POPUP_KEY) != null) {
        this.$localStorage.clear(SESSION_EXPIRE_POPUP_KEY);
      }
      return;
    }

    if (this.$localStorage.retrieve(SESSION_EXPIRE_POPUP_KEY) != null) {
      let sessExpireAt = moment(this.$localStorage.retrieve(SESSION_EXPIRE_POPUP_KEY)).diff(moment(), 'seconds') || 1;
      if (sessExpireAt < 0) {
        sessExpireAt = 0;
      }

      this.ngZone.runOutsideAngular(() => {
        this.activeSessionTimeoutSubscription = of(new AccountPanelEvent('session_expires'))
          .pipe(delay(sessExpireAt * AuthServerProvider.MILLISECOND_MULTIPLIER))
          .subscribe(e => {
            this.ngZone.run(() => {
              this.eventsSubject.next(e);
            });
          });
      });
    }
  }

  /**
   * Stop session expiration timer
   */
  clearCloseSessionTimer(): void {
    if (this.activeSessionTimeoutSubscription) {
      this.activeSessionTimeoutSubscription.unsubscribe();
    }
  }

  /**
   * Logout user and send user to login page
   */
  logout(noRedirectToLogoutUrl = false): Observable<void> {
    return new Observable(observer => {
      this.stateStorage.clearCompany();
      // rimosso per prod this.sessionStorage.clear('scriptAdServerClose');
      this.oauthService.logOut(noRedirectToLogoutUrl);
      observer.complete();
    });
  }



  private redirectToErrorPage() {
    const chekRedirectUr = window.location.host + window.location.pathname;
    const docaposteUri = this.appConfig.docaposteUri ? this.appConfig.docaposteUri : '';
    const {
      docapostegenericErrorUrl = '',
      genericErrorUrl = '',
      authConfig: { clientId = '' },
    } = this.appConfig;
    if (chekRedirectUr.includes(docaposteUri)) {
      redirectToGlobalErrorPage(docapostegenericErrorUrl, clientId, '0002');
    } else {
      redirectToGlobalErrorPage(genericErrorUrl, clientId, '0002');
    }
  }

  // Check if user just logged, not first login
  private userJustLoggedIn() {
    return this.oauthService.hasValidIdToken() && this.oauthService.hasValidAccessToken();
  }

  private hasInvalidToken() {
    return !this.oauthService.hasValidIdToken() || !this.oauthService.hasValidAccessToken();
  }
  private redirectToLoggedCompany(companyName: string) {
    let redirectUrl = window.location.pathname.replace(this.appConfig.baseHref, '') + window.location.search;
    const loggedCompany = this.stateStorage.getLoggedCompany();

    if (loggedCompany !== companyName) {
      redirectUrl = redirectUrl.replace(companyName, loggedCompany);
      this.oauthService.redirectUri = `${window.location.protocol}//${window.location.host}/${redirectUrl}`;
    }

    this.stateStorage.storeUrl(redirectUrl);
  }

  private handleLoginFailure() {
    this.stateStorage.storeUrl(window.location.pathname.replace(this.appConfig.baseHref, '') + window.location.search);
    this.stateStorage.clearCompany();
    this.oauthService.logOut(true);
    this.oauthService.initCodeFlow();
  }

  private handleLoginSuccess() {
    const script = `{window.dataLayer = window.dataLayer || []; dataLayer = [{ 'environment': '${this.appConfig.env}','platform': 'panel','lang': '${this.languageService.currentLang}'}];}`;
    this.scriptService.loadJsScript('dataLayer', script, null);

    const scriptmg =
      "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=" +
      "'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-PNJ4BPD');";

    this.scriptService.loadJsScript('gtm', scriptmg, null);
  }

  private handleLoginError(companyName: string) {
    this.stateStorage.storeUrl(window.location.pathname.replace(this.appConfig.baseHref, '') + window.location.search);

    let genericError = this.appConfig.genericErrorUrl;
    if (companyName === this.appConfig.docaposteUri) {
      genericError = this.appConfig.docapostegenericErrorUrl;
    }
    this.stateStorage.clearCompany();
    redirectToLogoutErrorPage(
      this.appConfig.authConfig.logoutUrl || '',
      genericError || '',
      this.appConfig.authConfig.clientId || '',
      '0003'
    );
    this.oauthService.logOut(true);
  }
}
